<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import MasifiWhatsappQrMessagesAPI from 'dashboard/api/masifi/masifiWhatsappQrMessages';
import { messageTimestamp } from 'shared/helpers/timeHelper';

// "Ver original" de un mensaje eliminado en una bandeja WhatsApp QR (follow-up del PR #192; spec
// whatsapp-qr-edit-revoke-design.md §4.9). Lo monta MasifiWaMessageNote SOLO con la marca `masifiWaOriginalKept` y para un
// administrador o el autor (el backend decide igual: 403). El original NO viaja con el mensaje: se pide al tocar, una vez.
// Mismo patrón visual que el "Ver original" de un editado (Button link nativo + bloque atenuado con borde). Fragmento: va
// dentro de la fila flex de la nota.
const props = defineProps({
  messageId: { type: Number, required: true },
});

const { t } = useI18n();
const original = ref(null);
const shown = ref(false);
const loading = ref(false);
const error = ref('');

// Solo NUESTRO formato ({ error, code }, ya traducido por el backend) se muestra tal cual. Sin él: 401/403/404 (gate del
// módulo, sesión, ya no eliminado) = sin acceso, que reintentar no arregla; el resto = genérico con "toca de nuevo".
const NO_ACCESS_STATUSES = [401, 403, 404];
const errorText = failure => {
  const response = failure?.response;
  if (response?.data?.code && response.data.error) return response.data.error;
  return NO_ACCESS_STATUSES.includes(response?.status)
    ? t('MASIFI_WHATSAPP_QR.MESSAGE_NOTE.DELETED_ORIGINAL.NO_ACCESS')
    : t('MASIFI_WHATSAPP_QR.MESSAGE_NOTE.DELETED_ORIGINAL.LOAD_ERROR');
};

const load = async () => {
  loading.value = true;
  error.value = '';
  try {
    const { data } = await MasifiWhatsappQrMessagesAPI.original(
      props.messageId
    );
    original.value = data;
    shown.value = true;
  } catch (failure) {
    error.value = errorText(failure);
  } finally {
    loading.value = false;
  }
};

const toggle = () => {
  if (loading.value) return;
  if (shown.value || original.value) {
    shown.value = !shown.value;
    return;
  }
  load();
};

const deletedLine = computed(() => {
  const { via, deleted_by_name: name, deleted_at: deletedAt } = original.value;
  const date = messageTimestamp(deletedAt, 'LLL d, h:mm a');
  if (via === 'phone')
    return t(
      'MASIFI_WHATSAPP_QR.MESSAGE_NOTE.DELETED_ORIGINAL.DELETED_FROM_PHONE',
      { date }
    );
  if (!name)
    return t('MASIFI_WHATSAPP_QR.MESSAGE_NOTE.DELETED_ORIGINAL.DELETED_AT', {
      date,
    });
  return via === 'everyone'
    ? t(
        'MASIFI_WHATSAPP_QR.MESSAGE_NOTE.DELETED_ORIGINAL.DELETED_FOR_EVERYONE_BY',
        { name, date }
      )
    : t('MASIFI_WHATSAPP_QR.MESSAGE_NOTE.DELETED_ORIGINAL.DELETED_BY', {
        name,
        date,
      });
});
</script>

<template>
  <Button
    link
    slate
    xs
    data-test="masifi-wa-deleted-original-toggle"
    :is-loading="loading"
    :label="
      shown
        ? t('MASIFI_WHATSAPP_QR.MESSAGE_NOTE.HIDE_ORIGINAL')
        : t('MASIFI_WHATSAPP_QR.MESSAGE_NOTE.SHOW_ORIGINAL')
    "
    @click="toggle"
  />
  <span
    v-if="error"
    role="alert"
    data-test="masifi-wa-deleted-original-error"
    class="text-n-ruby-11"
  >
    {{ error }}
  </span>
  <template v-if="shown && original">
    <span class="basis-full" />
    <div
      data-test="masifi-wa-deleted-original"
      class="flex flex-col max-w-full gap-0.5 mt-1 border-s-2 border-n-weak ps-2"
    >
      <p
        v-if="original.content"
        data-test="masifi-wa-deleted-original-content"
        class="mb-0 text-sm whitespace-pre-line break-words"
      >
        {{ original.content }}
      </p>
      <p v-if="original.attachments_count" class="mb-0">
        {{
          t('MASIFI_WHATSAPP_QR.MESSAGE_NOTE.DELETED_ORIGINAL.ATTACHMENTS', {
            count: original.attachments_count,
          })
        }}
      </p>
      <p class="mb-0">{{ deletedLine }}</p>
    </div>
  </template>
</template>
